import {
  getCallsToAction,
  getRetailerAdCreativeTemplate,
} from '@promoboxx/redux-stores/src/stores/facebook-ads/actions'
import {
  callsToActionSelector,
  getRetailerAdCreativeTemplatesSelector,
} from '@promoboxx/redux-stores/src/stores/facebook-ads/selectors'
import { embedSocialPreview } from '@promoboxx/social-preview'
import { connect } from 'formik'
import { useRef, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { adPreviewTypes } from '../lib/adPreviewTypes'
import cloudinary from '../lib/Cloudinary'
import useCombinedPremiumAdData from '../lib/useCombinedPremiumAdData'
import {
  brandsById,
  retailerIdsByBrandIdSelector,
} from '../stores/app/selectors'
import { facebookPagesById } from '../stores/facebookPages/selectors'

const PaidAdSocialPreview = (props) => {
  const campaignData = useCombinedPremiumAdData(props.campaignId)
  const previewRef = useRef()
  const formCreative = props.formik.values.adCreativeTemplate
  const facebookPages = useSelector(facebookPagesById)
  const facebookPage = facebookPages[props.formik.values.facebookPageId]
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(getCallsToAction())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const callsToAction = useSelector(callsToActionSelector)

  const brand = useSelector((state) => {
    if (!campaignData.campaign) {
      return null
    }

    return brandsById(state)[campaignData.campaign.brand_id]
  })

  const retailerId = useSelector((state) => {
    if (!brand) {
      return null
    }

    return retailerIdsByBrandIdSelector(state)[brand.id]
  })

  useEffect(() => {
    dispatch(
      getRetailerAdCreativeTemplate({
        id: campaignData.adCreativeTemplate.id,
        retailerId,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    campaignData.adCreativeTemplate && campaignData.adCreativeTemplate.id,
    retailerId,
  ])

  const adCreateTemplateWithReplacementsAndExtraData = useSelector((state) => {
    if (!campaignData.adCreativeTemplate || !retailerId) {
      return null
    }

    return getRetailerAdCreativeTemplatesSelector(
      state,
      campaignData.adCreativeTemplate.id,
      retailerId,
    )
  })

  const socialPreviewOptions = useMemo(() => {
    if (!formCreative || !adCreateTemplateWithReplacementsAndExtraData) {
      return null
    }

    let media
    const safeFacebookPage = facebookPage || {}
    const video_data =
      adCreateTemplateWithReplacementsAndExtraData &&
      adCreateTemplateWithReplacementsAndExtraData.object_story_spec &&
      adCreateTemplateWithReplacementsAndExtraData.object_story_spec.video_data
    const link_data =
      adCreateTemplateWithReplacementsAndExtraData &&
      adCreateTemplateWithReplacementsAndExtraData.object_story_spec &&
      adCreateTemplateWithReplacementsAndExtraData.object_story_spec.link_data

    if (formCreative.creative_type === 'carousel') {
      media = formCreative.creative.child_attachments.map(
        (childAttachment, i) => {
          const childAttachmentWithExtraData =
            adCreateTemplateWithReplacementsAndExtraData.object_story_spec
              .link_data.child_attachments[i]
          const mediaType = childAttachment.media.media_type
          const mediaObject =
            mediaType === 'image'
              ? childAttachment.media
              : {
                  ...childAttachment.media,
                  source_id: childAttachmentWithExtraData.cloudinary_id,
                }

          return {
            mediaType,
            mediaSrc: cloudinaryUrlWithCrops(mediaObject),
            url: childAttachment.link.url,
            callToAction: callsToAction[childAttachment.call_to_action.cta_type]
              ? callsToAction[childAttachment.call_to_action.cta_type].value
              : childAttachment.call_to_action.cta_type,
            headline: childAttachmentWithExtraData.name,
            description: childAttachmentWithExtraData.description,
          }
        },
      )
    } else {
      const mediaType = formCreative.creative.media.media_type
      const mediaObject =
        mediaType === 'image'
          ? formCreative.creative.media
          : {
              ...formCreative.creative.media,
              source_id:
                (link_data && link_data.cloudinary_id) ||
                (video_data && video_data.cloudinary_id),
            }

      media = [
        {
          mediaType,
          mediaSrc: cloudinaryUrlWithCrops(mediaObject),
          url: formCreative.creative.link.url,
          callToAction: callsToAction[
            formCreative.creative.call_to_action.cta_type
          ]
            ? callsToAction[formCreative.creative.call_to_action.cta_type].value
            : formCreative.creative.call_to_action.cta_type,
          headline:
            (link_data && link_data.name) || (video_data && video_data.title),
          description:
            (link_data && link_data.description) ||
            (video_data && video_data.link_description),
        },
      ]
    }

    return {
      type: adPreviewTypes[props.type].socialPreviewType,
      payload: {
        userFullName: safeFacebookPage.name,
        userHandle: safeFacebookPage.name,
        userAvatar: safeFacebookPage.picture_url,
        primaryText:
          (link_data && link_data.message) ||
          (video_data && video_data.message),
        carouselIndex: props.formik.values.carouselIndex,
        media,
      },
      events: {
        'socialPreview/carouselIndexUpdated': (event) => {
          props.formik.setFieldValue(
            'carouselIndex',
            event.data.payload.carouselIndex,
          )
        },
      },
      align: 'center',
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formCreative,
    props.type,
    props.formik.values.carouselIndex,
    facebookPage,
    adCreateTemplateWithReplacementsAndExtraData,
  ])

  useEffect(() => {
    if (!previewRef.current || !socialPreviewOptions) {
      return
    }

    embedSocialPreview(previewRef.current, socialPreviewOptions)
  }, [socialPreviewOptions])

  return <div ref={previewRef} />
}

const cloudinaryUrlWithCrops = (creativeMedia) => {
  if (creativeMedia.media_type !== 'image') {
    return cloudinary.instance().video_url(creativeMedia.source_id, {
      transformations: [
        {
          quality: 'auto',
        },
      ],
    })
  }

  /** @type {cloudinary.Transformation.Options} */
  const options = {
    transformation: [
      {
        width: 1280,
        crop: 'scale',
        format: 'jpg',
        quality: 'auto',
      },
    ],
  }

  let crop
  if (
    creativeMedia.image_crops &&
    Object.keys(creativeMedia.image_crops).length
  ) {
    crop = creativeMedia.image_crops[Object.keys(creativeMedia.image_crops)[0]]
  }

  if (crop) {
    // Grab the first crop.
    // TODO No idea what is proper here, may need like a whole bunch more for
    // context.
    let transformation = {}
    transformation.crop = 'crop'
    transformation.x = crop.left_top[0]
    transformation.y = crop.left_top[1]
    transformation.width = crop.right_bottom[0] - crop.left_top[0]
    transformation.height = crop.right_bottom[1] - crop.left_top[1]

    options.transformation.unshift(transformation)
  }

  return cloudinary.instance().url(creativeMedia.source_id, options)
}

export default connect(PaidAdSocialPreview)
